import React from "react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";
import { match } from "ts-pattern";

import type { TYooKassaPaymentStatus } from "~/api";
import { isQueryNotFoundError } from "~/common/query";
import { BadgeAlert, CircleCheck, CircleX } from "~/common/redesign-v1/icons";
import { Loader } from "~/components/ui/loader";

export function PaymentStatusMessage({
  query,
}: {
  query: {
    isUninitialized: boolean;
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    data?: { status: TYooKassaPaymentStatus };
    error?: unknown;
  };
}) {
  let content;

  if (query.isUninitialized || query.isLoading) {
    content = <Loader />;
  }

  if (query.isError) {
    if (isQueryNotFoundError(query.error)) {
      content = (
        <>
          <CircleX className="tw-size-5 tw-text-red-600" />
          <span className="tw-font-semibold">
            <FormattedMessage id="pat.payments.course.status.not_found" />
          </span>
        </>
      );
    } else {
      content = (
        <>
          <CircleX className="tw-size-5 tw-text-red-600" />
          <span className="tw-font-semibold">
            <FormattedMessage id="pat.payments.course.status.error" />
          </span>
        </>
      );
    }
  }

  if (query.isSuccess && query.data) {
    content = match(query.data.status)
      .with("succeeded", () => (
        <>
          <CircleCheck className="tw-size-5 tw-text-green-600" />
          <span className="tw-font-semibold">
            <FormattedMessage id="pat.payments.course.paid" />
          </span>
        </>
      ))
      .with("pending", () => (
        <>
          {/* Same warning glyph as the «Ждут действий» patient status. */}
          <BadgeAlert className="tw-size-5 tw-text-orange-500" />
          <span className="tw-font-semibold">
            <FormattedMessage id="pat.payments.course.status.pending" />
          </span>
        </>
      ))
      .with("canceled", () => (
        <>
          <CircleX className="tw-size-5 tw-text-red-600" />
          <span className="tw-font-semibold">
            <FormattedMessage id="pat.payments.course.status.canceled" />
          </span>
        </>
      ))
      .exhaustive();
  }

  return <div className="tw-flex tw-items-center tw-gap-2 max-desktop:tw-justify-center">{content}</div>;
}

/**
 * Redesign presentation for a payment-return page. `bareLayout` supplies the redesign shell, while this
 * component supplies a centered title, status row and optional action without the legacy `Portlet` markup.
 */
export function RedesignPaymentStatusCard({ titleId, query, action }: { titleId: MessageDescriptor["id"]; query: Parameters<typeof PaymentStatusMessage>[0]["query"]; action?: React.ReactNode }) {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-8 tw-text-center">
      <h1 className="tw-m-0 tw-font-rd-accent tw-text-xl tw-font-normal tw-text-foreground">
        <FormattedMessage id={titleId} />
      </h1>
      <PaymentStatusMessage query={query} />
      {action}
    </div>
  );
}
